import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Save, ShieldCheck, KeyRound, Plug, Database, History, Webhook as WebhookIcon } from "lucide-react";

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 sm:p-6 space-y-4" data-testid={testId}>
      <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
        <Icon className="w-4 h-4 text-slate-700" />
        <h2 className="text-sm font-bold tracking-tight uppercase" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function RowItem({ label, value, mono = true, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center" data-testid={testId}>
      <Label className="text-xs font-semibold text-slate-700 col-span-1">{label}</Label>
      <div className={`col-span-2 ${mono ? "font-mono" : ""} text-xs text-slate-900 break-all`}>{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "medium" }); }
  catch { return iso; }
}

export default function Configuration() {
  const [cfg, setCfg] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("queue");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cfgData }, { data: auditData }] = await Promise.all([
        api.get("/admin/config"),
        api.get("/admin/config/audit?limit=50"),
      ]);
      setCfg(cfgData);
      setMode(cfgData.adapter_mode);
      setAudit(auditData.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaveMode = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/config/mode", { mode, reason: reason.trim() || undefined });
      toast.success(`Modo guardado en BD: ${data.previous_mode} → ${data.adapter_mode}`);
      setReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="max-w-4xl">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
          Configuración
        </h1>
        <p className="text-sm text-slate-600 mt-0.5 mb-6">
          Parámetros del adaptador, credenciales y partners. Los valores sensibles aparecen enmascarados.
        </p>

        {loading ? (
          <div className="text-sm text-slate-500" data-testid="config-loading">Cargando configuración…</div>
        ) : (
          <div className="space-y-5">

            {/* Source-of-truth banner */}
            <div
              className="flex items-start gap-2 p-3 rounded-md bg-slate-900 text-white"
              data-testid="source-of-truth-banner"
            >
              <Database className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="text-xs leading-relaxed">
                <div className="font-bold uppercase tracking-wider">Fuente de verdad del modo del adaptador</div>
                <p className="text-slate-300 mt-0.5">
                  <code className="font-mono text-emerald-300">.env</code> sólo siembra al primer arranque;
                  en runtime <strong>manda la BD</strong>. Tras un reinicio, el bridge restaura el último modo persistido.
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono">
                  <span>persistido_en_bd: <span className={cfg.adapter_mode_persisted_in_db ? "text-emerald-300" : "text-amber-300"}>
                    {cfg.adapter_mode_persisted_in_db ? "sí" : "no (aún en .env)"}
                  </span></span>
                  <span>valor_en_bd: <span className="text-emerald-300">{cfg.adapter_mode_db_value || "—"}</span></span>
                  <span>valor_env_default: <span className="text-slate-400">{cfg.adapter_mode_env_default || "—"}</span></span>
                  <span>modo_activo: <span className="text-emerald-300 font-bold">{cfg.adapter_mode}</span></span>
                </div>
              </div>
            </div>

            <Section title="Adaptador InsuranceSoft" icon={Plug} testId="section-adapter">
              <div className="grid grid-cols-3 gap-3 items-start">
                <Label className="text-xs font-semibold text-slate-700 mt-2">Modo activo</Label>
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <Select value={mode} onValueChange={setMode}>
                      <SelectTrigger className="w-48 h-9 text-sm" data-testid="adapter-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="queue" data-testid="adapter-mode-option-queue">queue (recomendado)</SelectItem>
                        <SelectItem value="api" data-testid="adapter-mode-option-api">api (HTTP)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={onSaveMode}
                      disabled={saving || mode === cfg.adapter_mode}
                      data-testid="adapter-mode-save"
                      className="h-9 bg-slate-900 hover:bg-slate-800 text-white text-xs"
                    >
                      <Save className="w-3.5 h-3.5 mr-1" /> Guardar en BD
                    </Button>
                  </div>
                  <div>
                    <Label htmlFor="mode-reason" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                      Motivo del cambio (opcional, para auditoría)
                    </Label>
                    <Input
                      id="mode-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="ej. InsuranceSoft live → conmutamos a api"
                      className="h-8 text-xs mt-1"
                      data-testid="adapter-mode-reason"
                      disabled={mode === cfg.adapter_mode}
                    />
                  </div>
                </div>
              </div>
              <RowItem testId="cfg-base-url" label="Base URL" value={cfg.insurancesoft_base_url || "«pendiente»"} />
              <RowItem testId="cfg-auth-token" label="Auth token" value={cfg.insurancesoft_auth_token_masked} />
              <RowItem testId="cfg-product-code" label="Product code" value={cfg.insurancesoft_product_code} />
              <RowItem testId="cfg-create-path" label="Create policy path" value={cfg.insurancesoft_create_policy_path} />
              <RowItem testId="cfg-cancel-path" label="Cancel policy path" value={cfg.insurancesoft_cancel_policy_path} />
              <RowItem
                testId="cfg-api-configured"
                label="API conectada"
                mono={false}
                value={
                  cfg.adapter_api_configured ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5" /> Sí
                    </span>
                  ) : (
                    <span className="text-amber-700 font-semibold">
                      No — usar modo queue
                    </span>
                  )
                }
              />
              {cfg.adapter_mode_last_change && (
                <RowItem
                  testId="cfg-last-mode-change"
                  label="Último cambio"
                  mono={false}
                  value={
                    <span className="text-xs">
                      <span className="font-mono">{cfg.adapter_mode_last_change.from_mode}</span>
                      {" → "}
                      <span className="font-mono font-bold text-slate-900">{cfg.adapter_mode_last_change.to_mode}</span>
                      <span className="text-slate-500"> · {fmtDate(cfg.adapter_mode_last_change.changed_at)}</span>
                      <span className="text-slate-500"> · por <span className="font-mono">{cfg.adapter_mode_last_change.changed_by}</span></span>
                    </span>
                  }
                />
              )}
            </Section>

            <Section title="Auditoría de cambios de modo" icon={History} testId="section-mode-audit">
              {audit.length === 0 ? (
                <p className="text-xs text-slate-500" data-testid="mode-audit-empty">
                  Sin cambios registrados todavía. El primer guardado quedará aquí.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Cuándo</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">De</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">A</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Por</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {audit.map((a, idx) => (
                        <TableRow key={`${a.changed_at}-${idx}`} data-testid={`audit-row-${idx}`}>
                          <TableCell className="text-xs font-mono text-slate-700">{fmtDate(a.changed_at)}</TableCell>
                          <TableCell className="text-xs font-mono">{a.from_mode}</TableCell>
                          <TableCell className="text-xs font-mono font-bold text-slate-900">
                            {a.to_mode}
                            {a.no_op && <span className="ml-1 text-[10px] text-slate-400 uppercase tracking-wider">(no-op)</span>}
                          </TableCell>
                          <TableCell className="text-xs font-mono">{a.changed_by}</TableCell>
                          <TableCell className="text-xs text-slate-700 max-w-[280px] truncate" title={a.reason || ""}>
                            {a.reason || <span className="text-slate-400">—</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Section>

            <Section title="Partners (Evolvo)" icon={KeyRound} testId="section-partners">
              <RowItem testId="cfg-evolvo-key" label="X-Partner-Api-Key" value={cfg.evolvo_api_key_masked} />
              <p className="text-[11px] text-slate-500">
                Las API keys de partner se gestionan vía variables de entorno (<code className="font-mono">EVOLVO_API_KEY</code>). Para rotarlas, modifica el <code className="font-mono">.env</code> y reinicia el backend.
              </p>
            </Section>

            <Section title="Webhooks salientes (Evolvo)" icon={WebhookIcon} testId="section-webhooks">
              <RowItem testId="cfg-webhook-url" label="webhook_url" value={cfg.evolvo_webhook_url || "«pendiente»"} />
              <RowItem testId="cfg-webhook-secret" label="webhook_secret" value={cfg.evolvo_webhook_secret_masked || "«pendiente»"} />
              <RowItem testId="cfg-webhook-enabled" label="habilitado" mono={false}
                value={cfg.webhooks_enabled
                  ? <span className="text-emerald-700 font-semibold">sí</span>
                  : <span className="text-amber-700 font-semibold">no</span>} />
              <RowItem testId="cfg-webhook-timeout" label="timeout" value={`${cfg.webhook_timeout_seconds}s`} />
              <p className="text-[11px] text-slate-500">
                Configura <code className="font-mono">EVOLVO_WEBHOOK_URL</code> y <code className="font-mono">EVOLVO_WEBHOOK_SECRET</code> en
                <code className="font-mono"> backend/.env</code> y reinicia. Después usa
                <a href="/webhooks" className="text-slate-900 underline underline-offset-2 ml-1">/webhooks → Enviar webhook de prueba</a>
                {" "}para que Evolvo verifique su receptor antes de meter tráfico real.
              </p>
            </Section>

            <Section title="Reintentos & seguridad" icon={ShieldCheck} testId="section-retry">
              <RowItem testId="cfg-retry-max" label="Reintentos máx." value={String(cfg.retry_max_attempts)} />
              <RowItem testId="cfg-retry-backoff" label="Backoff base (s)" value={String(cfg.retry_backoff_base_seconds)} />
              <RowItem testId="cfg-hmac" label="HMAC requerido" value={cfg.hmac_required ? "sí" : "no (opcional)"} />
            </Section>

            <p className="text-[11px] text-slate-400 font-mono pt-2">
              El modo del adaptador se persiste en MongoDB (colección <code>settings</code>) y se audita en <code>mode_audit_log</code>.
              El resto de credenciales viven en <code>backend/.env</code>.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
